/**
 * Timezone-safe date parsing for user-entered / stored calendar dates.
 *
 * `Date.parse('2026-08-10')` is treated as UTC MIDNIGHT per the ECMA
 * spec, which is the PREVIOUS calendar day for every timezone west of
 * Greenwich — a saved "Aug 10" renders as "Aug 9" across the Americas.
 * Parsing at local noon keeps the calendar date stable in every
 * timezone (no inhabited timezone is ±12h from its own noon).
 *
 * Use this for ANY 'YYYY-MM-DD' string. Full ISO timestamps pass
 * through untouched.
 */
export function parseLocalDate(s: string | null | undefined): number {
  if (!s) return NaN;
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return Date.parse(t + 'T12:00:00');
  return Date.parse(t);
}

/**
 * Format a timestamp as its LOCAL calendar date ('YYYY-MM-DD'). The
 * inverse of parseLocalDate — use it instead of
 * `new Date(ms).toISOString().slice(0, 10)`, which renders the UTC
 * date: for users east of Greenwich, a date picker returning local
 * midnight would land on the PREVIOUS day.
 */
export function toLocalYMD(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
